/** Accuracy mode trust strip, abstention, and debug helpers (Phase 3 Slice 1). */

export const INSUFFICIENT_EVIDENCE_TITLE = 'Insufficient Evidence';
export const INSUFFICIENT_EVIDENCE_BODY =
    'The available documentary evidence was not sufficient to answer confidently.';

/** Backend `INSUFFICIENT_EVIDENCE_ANSWER` prefix (citation_governor). */
export const INSUFFICIENT_EVIDENCE_ANSWER_PREFIX =
    'There is insufficient evidence in the retrieved project sources to answer';

const BAND_LABELS = {
    high: 'Strong Evidence',
    medium: 'Moderate Evidence',
    low: 'Weak Evidence',
};

const ABSTENTION_GROUNDING_STATUSES = new Set([
    'blocked_no_proof_evidence',
    'blocked_no_evidence',
    'blocked_weak_evidence',
]);

export const ACCURACY_INACTIVE_REASON_MASTER_FLAG = 'Backend master flag disabled';

/** Consultant-facing Ask section order (answer first). */
export const ACCURACY_ASK_UI_ORDER = ['answer', 'trustStrip', 'evidence', 'debug'];

export function formatConfidenceBandLabel(band) {
    if (band == null || band === '') return null;
    const key = String(band).toLowerCase().trim();
    return BAND_LABELS[key] || String(band);
}

export function normalizeSourceMixCounts(sourceMix) {
    const mix = sourceMix && typeof sourceMix === 'object' ? sourceMix : {};
    const proof =
        Number(mix.vdr_chunk ?? mix.VDR_CHUNK ?? mix.vdr ?? mix.VDR ?? 0) || 0;
    const management = Number(mix.irl ?? mix.IRL ?? 0) || 0;
    const consultant = Number(mix.observation ?? mix.OBSERVATION ?? 0) || 0;
    return { proof, management, consultant, total: proof + management + consultant };
}

function pickDecomposed(decomposed) {
    if (!decomposed || typeof decomposed !== 'object' || !decomposed.active) return null;
    return decomposed;
}

export function formatCoveragePercent(citationCoverage) {
    if (citationCoverage == null || citationCoverage === '') return null;
    const n = Number(citationCoverage);
    if (!Number.isFinite(n)) return null;
    const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
    return `${pct}%`;
}

/**
 * Backend truth for accuracy mode flags (retrieval_debug is source of truth for Active).
 * requested=true with active=false usually means INTELLIGENCE_ACCURACY_FIRST_MODE is off on the API host.
 */
export function resolveAccuracyModeStatus(retrievalDebug) {
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const requested = debug.accuracy_mode_requested === true;
    const active = debug.accuracy_mode_active === true;
    let inactiveReason = null;

    if (requested && !active) {
        const eff =
            debug.effective_flags && typeof debug.effective_flags === 'object'
                ? debug.effective_flags
                : null;
        if (
            eff?.accuracy_mode_active === false ||
            eff?.INTELLIGENCE_ACCURACY_FIRST_MODE === false
        ) {
            inactiveReason = ACCURACY_INACTIVE_REASON_MASTER_FLAG;
        } else if (typeof debug.note === 'string' && debug.note.trim()) {
            inactiveReason = debug.note.trim();
        } else {
            inactiveReason = ACCURACY_INACTIVE_REASON_MASTER_FLAG;
        }
    }

    return { requested, active, inactiveReason, pipelineRan: active };
}

/**
 * Build trust-strip rows when coverage_report and/or decomposed_confidence exist.
 * Renders partial rows when only some fields are present.
 */
export function extractAccuracyTrustStrip({
    coverageReport,
    decomposedConfidence,
    retrievalDebug,
    topLevelConfidence,
}) {
    const modeStatus = resolveAccuracyModeStatus(retrievalDebug);
    if (modeStatus.requested && !modeStatus.active) {
        return {
            items: [
                {
                    key: 'accuracy_mode',
                    label: 'Accuracy Mode',
                    value: 'Inactive',
                    detail: modeStatus.inactiveReason || ACCURACY_INACTIVE_REASON_MASTER_FLAG,
                },
            ],
            sourceMix: normalizeSourceMixCounts(retrievalDebug?.source_mix),
            modeStatus,
            pipelineActive: false,
        };
    }

    const coverage =
        coverageReport && typeof coverageReport === 'object'
            ? coverageReport
            : retrievalDebug?.coverage_report;
    const decomposed = pickDecomposed(
        decomposedConfidence ?? coverage?.decomposed_confidence ?? retrievalDebug?.decomposed_confidence,
    );

    const sourceMix = normalizeSourceMixCounts(coverage?.source_mix ?? retrievalDebug?.source_mix);
    const hasCoverageMetric =
        coverage?.citation_coverage != null ||
        coverage?.final_citation_coverage != null ||
        retrievalDebug?.citation_coverage != null;
    const hasConfidenceBands = Boolean(decomposed?.evidence?.band || decomposed?.overall?.band || topLevelConfidence);

    if (!hasCoverageMetric && !hasConfidenceBands && sourceMix.total === 0) return null;
    const coveragePct = formatCoveragePercent(
        coverage?.citation_coverage ??
            coverage?.final_citation_coverage ??
            retrievalDebug?.citation_coverage,
    );

    const evidenceBand = decomposed?.evidence?.band;
    const overallBand = decomposed?.overall?.band ?? topLevelConfidence;

    const abstentionState = resolveAbstentionStateLabel({
        coverageReport: coverage,
        retrievalDebug,
    });

    const items = [];
    if (coveragePct != null) {
        items.push({ key: 'coverage', label: 'Coverage', value: coveragePct });
    }
    if (evidenceBand) {
        items.push({
            key: 'evidence_confidence',
            label: 'Evidence Confidence',
            value: formatConfidenceBandLabel(evidenceBand),
            band: evidenceBand,
        });
    }
    if (overallBand) {
        items.push({
            key: 'overall_confidence',
            label: 'Overall Confidence',
            value: formatConfidenceBandLabel(overallBand),
            band: String(overallBand).toLowerCase(),
        });
    }
    items.push({
        key: 'proof_sources',
        label: 'Proof Sources',
        value: String(sourceMix.proof),
    });
    items.push({
        key: 'context_sources',
        label: 'Context Sources',
        value: String(sourceMix.management + sourceMix.consultant),
        detail:
            sourceMix.management || sourceMix.consultant
                ? `Management ${sourceMix.management}, Consultant ${sourceMix.consultant}`
                : null,
    });
    if (abstentionState) {
        items.push({ key: 'abstention', label: 'Abstention State', value: abstentionState });
    }

    if (items.length === 0) return null;

    return { items, sourceMix, modeStatus, pipelineActive: true };
}

function resolveAbstentionStateLabel({ coverageReport, retrievalDebug }) {
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const coverage = coverageReport && typeof coverageReport === 'object' ? coverageReport : {};

    if (coverage.strict_abstention_applied || debug.strict_abstention_applied) {
        return 'Strict abstention applied';
    }
    if (debug.confidence_abstention_applied) {
        return 'Blocked: low confidence';
    }

    const status = String(
        coverage.grounding_status ?? debug.grounding_status ?? '',
    )
        .toLowerCase()
        .trim();

    if (status === 'blocked_no_proof_evidence') return 'Blocked: no proof evidence';
    if (status === 'blocked_no_evidence') return 'Blocked: no evidence';
    if (status === 'blocked_weak_evidence') return 'Blocked: weak evidence';
    if (ABSTENTION_GROUNDING_STATUSES.has(status)) {
        return status.replace(/_/g, ' ');
    }
    return 'None';
}

export function isInsufficientEvidenceAnswer(answer) {
    const text = String(answer || '').trim();
    if (!text) return false;
    if (text.startsWith(INSUFFICIENT_EVIDENCE_ANSWER_PREFIX)) return true;
    return text.toLowerCase().includes('insufficient evidence in the retrieved project sources');
}

/**
 * Detect abstention UX for INSUFFICIENT_EVIDENCE_ANSWER, blocked_no_proof_evidence,
 * and confidence-based abstention (blocked_low_confidence alias).
 */
export function resolveIntelligenceAbstention({
    answer,
    retrievalDebug,
    evidenceGovernance,
    coverageReport,
}) {
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const gov = evidenceGovernance && typeof evidenceGovernance === 'object' ? evidenceGovernance : {};
    const coverage =
        coverageReport && typeof coverageReport === 'object'
            ? coverageReport
            : debug.coverage_report;

    const grounding = String(
        gov.grounding_status ?? coverage?.grounding_status ?? debug.grounding_status ?? '',
    )
        .toLowerCase()
        .trim();

    const reasons = [];

    if (isInsufficientEvidenceAnswer(answer)) {
        reasons.push('insufficient_evidence_answer');
    }
    if (grounding === 'blocked_no_proof_evidence') {
        reasons.push('blocked_no_proof_evidence');
    }
    if (debug.confidence_abstention_applied) {
        reasons.push('blocked_low_confidence');
    }

    const isAbstention = reasons.length > 0;

    return {
        isAbstention,
        reasons,
        title: INSUFFICIENT_EVIDENCE_TITLE,
        body: INSUFFICIENT_EVIDENCE_BODY,
    };
}

export function hasAccuracyTrustData(retrievalDebug) {
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const coverage = debug.coverage_report;
    const decomposed = debug.decomposed_confidence;
    const sourceMix = normalizeSourceMixCounts(debug.source_mix ?? coverage?.source_mix);
    return Boolean(
        (coverage && typeof coverage === 'object') ||
            (decomposed && typeof decomposed === 'object' && decomposed.active) ||
            sourceMix.total > 0 ||
            debug.citation_coverage != null,
    );
}

/** Show trust strip when user enabled accuracy for this ask (partial metrics or inactive status OK). */
export function shouldShowAccuracyTrustStrip({ accuracyModeEnabled, retrievalDebug }) {
    if (accuracyModeEnabled !== true) return false;
    const status = resolveAccuracyModeStatus(retrievalDebug);
    if (status.requested && !status.active) return true;
    return hasAccuracyTrustData(retrievalDebug);
}

/** Collapsed debug panel sections (accuracy mode only). */
export function buildAccuracyDebugSnapshot(retrievalDebug) {
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const decomposed = debug.decomposed_confidence;
    const coverage = debug.coverage_report;
    const multipass = debug.accuracy_multipass || coverage?.accuracy_multipass;
    const evidenceFilter = debug.evidence_filter || coverage?.evidence_filter;
    const authorityRerank = debug.authority_rerank || coverage?.authority_rerank;

    const sections = [];

    const routerIntent = debug.router_intent ?? debug.intent ?? debug.primary_intent;
    if (routerIntent) {
        sections.push({ title: 'Router intent', lines: [String(routerIntent)] });
    }

    const mix = normalizeSourceMixCounts(debug.source_mix ?? coverage?.source_mix);
    sections.push({
        title: 'Source mix',
        lines: [
            `Proof (VDR): ${mix.proof}`,
            `Management context (IRL): ${mix.management}`,
            `Consultant context (Observations): ${mix.consultant}`,
        ],
    });

    if (multipass && typeof multipass === 'object') {
        const passes = Array.isArray(multipass.passes) ? multipass.passes : [];
        const passLines = passes.length
            ? passes.map((p, i) => {
                  const name = p?.pass_name ?? p?.name ?? `pass_${i + 1}`;
                  const raw = p?.raw_counts_by_pass ?? p?.raw_count ?? '—';
                  return `${name}: raw=${JSON.stringify(raw)}`;
              })
            : [`active: ${Boolean(multipass.active)}`];
        const contradiction = multipass.contradiction_candidates_count;
        if (contradiction != null) {
            passLines.push(`Contradiction candidates: ${contradiction}`);
        }
        sections.push({ title: 'Multipass', lines: passLines });
    }

    if (evidenceFilter && typeof evidenceFilter === 'object') {
        const lines = [];
        if (evidenceFilter.active != null) lines.push(`Active: ${evidenceFilter.active}`);
        if (evidenceFilter.proof_sources_present != null) {
            lines.push(`Proof sources present: ${evidenceFilter.proof_sources_present}`);
        }
        if (evidenceFilter.filtered_out_count != null) {
            lines.push(`Filtered out: ${evidenceFilter.filtered_out_count}`);
        }
        if (evidenceFilter.input_count != null) {
            lines.push(`Input count: ${evidenceFilter.input_count}`);
        }
        if (lines.length) sections.push({ title: 'Evidence filter', lines });
    }

    if (authorityRerank && typeof authorityRerank === 'object') {
        const lines = [];
        if (authorityRerank.active != null) lines.push(`Active: ${authorityRerank.active}`);
        const summary = authorityRerank.summary;
        if (summary && typeof summary === 'object') {
            lines.push(`Summary: ${JSON.stringify(summary)}`);
        } else if (authorityRerank.reranked_count != null) {
            lines.push(`Reranked: ${authorityRerank.reranked_count}`);
        }
        if (lines.length) sections.push({ title: 'Authority rerank', lines });
    }

    if (decomposed?.active) {
        for (const axis of ['retrieval', 'evidence', 'synthesis', 'overall']) {
            const ax = decomposed[axis];
            if (!ax || typeof ax !== 'object') continue;
            const band = ax.band ? formatConfidenceBandLabel(ax.band) : '—';
            const reasons = Array.isArray(ax.reasons) ? ax.reasons.join('; ') : '';
            sections.push({
                title: `Confidence: ${axis}`,
                lines: [`${band} (score ${ax.score ?? '—'})`, reasons].filter(Boolean),
            });
        }
    }

    if (debug.accuracy_mode_active != null || debug.accuracy_mode_requested != null) {
        const modeStatus = resolveAccuracyModeStatus(debug);
        const lines = [
            `Requested: ${debug.accuracy_mode_requested ?? '—'}`,
            `Active: ${debug.accuracy_mode_active ?? '—'}`,
        ];
        if (modeStatus.inactiveReason) {
            lines.push(`Reason: ${modeStatus.inactiveReason}`);
        }
        sections.push({
            title: 'Accuracy mode',
            lines,
        });
    }

    return sections;
}

export function trustStripToneForBand(band) {
    const key = String(band || '').toLowerCase();
    if (key === 'high') return 'success';
    if (key === 'medium') return 'warning';
    if (key === 'low') return 'danger';
    return 'neutral';
}
